// Zero-dependency TOTP (RFC 6238) for the Done-For-You mailbox drawer's live Google Auth Code.
// Uses the browser's Web Crypto (HMAC-SHA1) to turn the base32 2FA secret we already get from
// Zapmail into the same rotating 6-digit code Google's Authenticator produces — no extra API calls.

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

// Decode an RFC 4648 base32 string (case/space/padding tolerant) into bytes
const base32ToBytes = (input) => {
    const clean = String(input || '').toUpperCase().replace(/[^A-Z2-7]/g, '');
    let bits = '';
    for (const char of clean) bits += BASE32_ALPHABET.indexOf(char).toString(2).padStart(5, '0');
    const bytes = [];
    for (let i = 0; i + 8 <= bits.length; i += 8) bytes.push(parseInt(bits.slice(i, i + 8), 2));
    return new Uint8Array(bytes);
};

// Generate the current TOTP code for a base32 secret. Throws if the secret isn't valid base32.
export const generateTotp = async (secret, { digits = 6, period = 30 } = {}) => {
    const key = base32ToBytes(secret);
    if (!key.length) throw new Error('Invalid 2FA secret');
    const counter = Math.floor(Date.now() / 1000 / period);
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setUint32(0, Math.floor(counter / 2 ** 32));
    view.setUint32(4, counter >>> 0);
    const cryptoKey = await window['crypto']['subtle'].importKey('raw', key, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign']);
    const hmac = new Uint8Array(await window['crypto']['subtle'].sign('HMAC', cryptoKey, buffer));
    const offset = hmac[hmac.length - 1] & 0xf;
    const binary = ((hmac[offset] & 0x7f) << 24) | ((hmac[offset + 1] & 0xff) << 16) | ((hmac[offset + 2] & 0xff) << 8) | (hmac[offset + 3] & 0xff);
    return String(binary % 10 ** digits).padStart(digits, '0');
};

// Seconds remaining in the current 30s TOTP window
export const totpSecondsLeft = (period = 30) => period - (Math.floor(Date.now() / 1000) % period);
